import type { AppColors } from './types';

/**
 * Financial, sober light palette for Análise de FIIs.
 *
 * Identity:
 * - Off-white / pearl surfaces (background, card, popover).
 * - Green-graphite text (#1a2e1a family).
 * - Forest green (#1e6b3a) as the primary action / brand color.
 * - Sage / mint green (#4a7c59 / #5f8a6f) as the secondary / supporting accent.
 * - Discrete warm amber (#a6691b) used ONLY where semantics require
 *   "warning"; never as the primary surface tone.
 * - Warm crimson (#a8344b) for danger; cool blue (#2563eb) for info.
 *   These semantic colors are NEVER replaced by brand greens.
 *
 * Goal: trustworthy, sober, financial-feel light theme. No neon, no
 * oversaturation, no "generic dashboard" look.
 */
export const appColorsLight: AppColors = {
  semantic: {
    success: '#1e6b3a',
    successStrong: '#14582b',
    warning: '#8b5a0f',
    warningStrong: '#5e3a0c',
    danger: '#a8344b',
    dangerStrong: '#7d1f33',
    info: '#2563eb',
    infoStrong: '#1d4ed8',
    violet: '#5b21b6',
    cyan: '#0e7490',
    slate: '#64748b',
    white: '#ffffff',
  },
  actions: {
    accentOutline: {
      text: '#5e3a0c',
      border: '#8b5a0f59',
      hoverText: '#3f2708',
      hoverBorder: '#8b5a0f80',
      hoverBg: '#8b5a0f14',
    },
    range: {
      selectedBg: '#1e6b3a',
      selectedBorder: '#1e6b3a',
      selectedText: '#fdfcf8',
      selectedHoverBg: '#14582b',
      selectedHoverBorder: '#14582b',
      unselectedText: '#3d4f3d',
      unselectedBorder: '#1a2e1a33',
      unselectedHoverText: '#1e6b3a',
      unselectedHoverBorder: '#1e6b3a80',
      unselectedHoverBg: '#1e6b3a0f',
    },
    compareButton: {
      bg: '#4a7c59',
      hoverBg: '#3d6749',
      text: '#fdfcf8',
    },
  },
  charts: {
    market: {
      ifix: '#1e6b3a',
      ibov: '#5b21b6',
      idiv: '#0e7490',
      cdi: '#8b5a0f',
      selicMeta: '#5e3a0c',
      ipcaMeta: '#14582b',
      selicEfetiva12m: '#a8344b',
      ipca12m: '#2563eb',
      igpm: '#0e7490',
      pib: '#5b21b6',
      dlsp: '#0e7490',
      dbgg: '#475569',
      ifixBrl: '#1e6b3a',
      ifixUsd: '#14582b',
      ifixEur: '#8b5a0f',
      ifixGbp: '#5b21b6',
      ivvbBrl: '#1e6b3a',
      ivvbUsd: '#14582b',
    },
    summaryHistory: {
      price: '#8b5a0f',
      vp: '#2563eb',
      dy: '#1e6b3a',
      pvp: '#5b21b6',
      benchmark: '#475569',
    },
    portfolioMix: {
      realEstate: '#1e6b3a',
      funds: '#5f8a6f',
      financialAssets: '#8b5a0f',
      equities: '#5b21b6',
    },
  },
  indicators: {
    label: {
      high: '#a8344b',
      medium: '#8b5a0f',
      low: '#1e6b3a',
      muted: 'hsl(var(--muted-foreground))',
    },
    distance: {
      near: '#1e6b3a',
      medium: '#8b5a0f',
      far: '#2563eb',
      muted: 'hsl(var(--muted-foreground))',
    },
    accent: {
      selic: '#2563eb',
      leverage: '#8b5a0f',
      distance: '#5b21b6',
      vacancy: '#0e7490',
      delinquency: '#a8344b',
    },
  },
  highlights: {
    topBorder: {
      primary: '#1e6b3a',
      success: '#14582b',
      warning: '#8b5a0f',
      purple: '#5b21b6',
    },
    progressBar: {
      primary: '#1e6b3a',
      warning: '#8b5a0f',
    },
  },
  modules: {
    comparadorSeries: ['#1e6b3a', '#5b21b6', '#0e7490', '#8b5a0f', '#a8344b'],
    priceHistorySeries: ['#475569', '#1e6b3a', '#8b5a0f', '#0e7490', '#5b21b6', '#a8344b'],
    simulation: {
      price: '#8b5a0f',
      reinvested: '#1e6b3a',
      shares: '#5b21b6',
      adjustedPrice: '#475569',
      dividends: '#2563eb',
      eventMarker: '#0e7490',
    },
    liquidity: {
      liquidity: '#0e7490',
      cotistas: '#2563eb',
      cotas: '#1e6b3a',
    },
    dividends: {
      average: '#94a3b8',
      price: '#2563eb',
      dy: '#1e6b3a',
      dyGuide: '#14582b',
      eventMarker: '#8b5a0f',
      eventMarkerHover: '#5e3a0c',
      corporateEventLine: '#64748b',
      corporateEventMarker: '#64748b',
    },
    risk: {
      good: '#1e6b3a',
      medium: '#8b5a0f',
      bad: '#a8344b',
      needle: '#8b5a0f',
      needleBorder: '#1e6b3a',
    },
  },
};
